import { Extension } from "@tiptap/core";
import { isHistoryTransaction } from "@tiptap/pm/history";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, type EditorState, type Transaction } from "@tiptap/pm/state";
import { Mapping, ReplaceStep } from "@tiptap/pm/transform";
import {
    groupTag,
    nodeTag,
    tagMarkup,
    TAG_CHANGE_META,
    type BlockTag,
} from "./groupFormat";

/**
 * Keep a heading a heading when its text is cleared OR replaced.
 *
 * When a heading's whole content is wiped, ProseMirror replaces the heading with
 * its schema default — a paragraph. Users hit this three ways:
 *   - a title: Ctrl+A then Delete (an `AllSelection` delete leaves the default
 *     empty paragraph);
 *   - a title: Ctrl+A then TYPE (the `AllSelection` is replaced by a fresh
 *     default paragraph holding the typed text — so the heading is lost even
 *     though the block isn't empty);
 *   - a table cell: Backspace the last char of an `<h3>` and that one cell
 *     reverts to a paragraph while its siblings stay `<h3>`.
 *
 * Three mechanisms keep the tag stable:
 *
 *  1. `addKeyboardShortcuts` — in a single-textblock field (a title), Ctrl/Cmd+A
 *     selects the block's TEXT, not the whole doc. Typing or deleting over a text
 *     selection stays INSIDE the heading, so it's never replaced by an
 *     `AllSelection` swap. This is what fixes Ctrl+A + type.
 *
 *  2. `restoreEmptiedBlock` — a safety net for the paths a text selection can't
 *     cover (e.g. Backspace emptying a heading inside a table cell). It converts
 *     an empty paragraph back to the heading it just was, in the SAME
 *     `appendTransaction`, so the node is never actually a paragraph and the
 *     toolbar readout never flickers. Fires only when the selection now sits in
 *     an empty paragraph that was a heading a tick ago — either the cursor's own
 *     block, or the single top-level block a select-all delete leaves. A new line
 *     made by Enter at the end of a heading is left a paragraph. It also undoes
 *     Tiptap's `clearDocument`, which resets a select-all-deleted Label / Small /
 *     aligned line to a plain paragraph: emptying a line never changes its tag.
 *
 *  3. `defaultTagOnReplaceAll` — in a multi-line field, select-all + type /
 *     Delete / paste of one line collapses the doc into one schema-default
 *     paragraph. It becomes the field's `defaultTag` instead, in the same
 *     `appendTransaction`. A multi-block paste keeps its structure.
 */
export const KeepHeadingOnEmpty = Extension.create<{
    defaultTag: BlockTag;
    groupKey?: string;
}>({
    name: "keepHeadingOnEmpty",

    addOptions() {
        return { defaultTag: "paragraph" };
    },

    addKeyboardShortcuts() {
        return {
            "Mod-a": ({ editor }) => {
                const { doc } = editor.state;
                // Only in a single-textblock field (a title). Elsewhere (bodies,
                // tables) fall through to the default whole-doc select-all.
                if (doc.childCount !== 1 || !doc.firstChild?.isTextblock)
                    return false;
                const from = 1;
                const to = Math.max(1, doc.content.size - 1);
                editor.commands.setTextSelection({ from, to });
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        const { defaultTag, groupKey } = this.options;
        const { editor } = this;
        return [
            new Plugin({
                appendTransaction(transactions, oldState, newState) {
                    if (!transactions.some((tr) => tr.docChanged)) return null;
                    // Undo/redo and content loads (store sync, group seed) set the
                    // doc on purpose — never rewrite them.
                    if (
                        transactions.some(
                            (tr) =>
                                isHistoryTransaction(tr) ||
                                tr.getMeta("preventUpdate")
                        )
                    )
                        return null;
                    return (
                        restoreEmptiedBlock(transactions, oldState, newState) ??
                        defaultTagOnReplaceAll(
                            transactions,
                            oldState,
                            newState,
                            // D7: a grouped field takes the group's tag, else its
                            // own first line's, else the registry default.
                            (oldFirst) =>
                                (groupKey
                                    ? (groupTag(groupKey, editor) ??
                                      nodeTag(oldFirst))
                                    : null) ?? defaultTag
                        )
                    );
                },
            }),
        ];
    },
});

function restoreEmptiedBlock(
    transactions: readonly Transaction[],
    oldState: EditorState,
    newState: EditorState
) {
    const $pos = newState.selection.$from;
    const block = $pos.parent;
    if (block.type.name !== "paragraph" || block.content.size !== 0)
        return null;

    // What was this block before the transaction?
    //  - Backspace inside a heading → the OLD cursor was in it.
    //  - Select-all + Delete → the old selection spans the whole doc
    //    (parent = doc), so fall back to the single top-level node the doc had.
    const oldParent = oldState.selection.$from.parent;
    const oldTop =
        oldState.doc.childCount === 1 ? oldState.doc.firstChild : null;
    const was = oldParent.isTextblock
        ? oldParent
        : oldTop?.isTextblock
          ? oldTop
          : null;
    if (!was || block.sameMarkup(was)) return null;
    // The author's own tag change (dropdown, group mirror) is never undone.
    if (transactions.some((tr) => tr.getMeta(TAG_CHANGE_META))) return null;

    // A heading is restored (e.g. an extra Backspace in an empty title). Other
    // markup only when another plugin reset it (Tiptap's clearDocument; a
    // plugin never sees its own appended transactions).
    const resetByPlugin = transactions.some((tr) =>
        tr.getMeta("appendedTransaction")
    );
    if (was.type.name !== "heading" && !resetByPlugin) return null;

    // Enter at the end of a heading also leaves the cursor in a new empty
    // paragraph. Only restore a block that existed before, as that same block.
    const mapping = new Mapping();
    transactions.forEach((tr) => mapping.appendMapping(tr.mapping));
    const old = mapping.invert().mapResult($pos.before(), -1);
    if (old.deleted || oldState.doc.nodeAt(old.pos) !== was) return null;

    return newState.tr.setNodeMarkup($pos.before(), was.type, was.attrs);
}

function defaultTagOnReplaceAll(
    transactions: readonly Transaction[],
    oldState: EditorState,
    newState: EditorState,
    tagFor: (oldFirst: PMNode | null) => BlockTag
) {
    const { doc } = newState;
    const block = doc.firstChild;
    if (doc.childCount !== 1 || block?.type.name !== "paragraph") return null;

    // Ctrl+A + type/Delete/Cut/paste in a multi-line field replaces the whole
    // doc (one ReplaceStep over 0..size). A drag-selection replaces less and
    // keeps the first line's tag; a toolbar command like list-off is a
    // ReplaceAroundStep. A single-line field keeps its own tag.
    const oldDoc = oldState.doc;
    const oldFirst = oldDoc.firstChild;
    if (oldDoc.childCount === 1 && oldFirst?.isTextblock) return null;
    const step = transactions[0]?.steps[0];
    const replacedAll =
        step instanceof ReplaceStep &&
        step.from === 0 &&
        step.to === oldDoc.content.size;
    if (!replacedAll) return null;

    const target = tagMarkup(newState.schema, tagFor(oldFirst));
    // Keep the first line's alignment / direction (e.g. a centered question).
    const { attrs } = target.type.create({
        ...(oldFirst?.isTextblock ? oldFirst.attrs : {}),
        ...target.attrs,
    });
    if (block.hasMarkup(target.type, attrs)) return null;
    return newState.tr.setNodeMarkup(0, target.type, attrs);
}
